import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { MapPin, Search, Building2, ArrowRight, X } from 'lucide-react';
import api from '@/utils/api';

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/public/properties').then(r => { setProperties(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = properties.filter(p => {
    const matchSearch = !search || (p.property_name || '').toLowerCase().includes(search.toLowerCase()) || (p.location || '').toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFilter || (p.property_type || '').toLowerCase() === typeFilter.toLowerCase();
    return matchSearch && matchType;
  });

  const types = [...new Set(properties.map(p => p.property_type).filter(Boolean))];

  return (
    <WebsiteLayout>
      <Helmet>
        <title>Properties | Plots, Flats, Villas for Sale | Ganesa Homestate</title>
        <meta name="description" content="Browse premium plots, flats, villas and commercial properties by Ganesa Homestate Developers. RERA approved with clear titles." />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#0A0A0F] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#DAA520] text-sm font-semibold uppercase tracking-wider">Our Properties</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3 mb-6">Find Your <span className="text-[#DAA520]">Perfect Property</span></h1>

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                data-testid="property-search"
                placeholder="Search by property name or location..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-[#1A1A25] border border-[#1A1A25] rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-[#DAA520]"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-4 py-3.5 bg-[#1A1A25] border border-[#1A1A25] rounded-xl text-white focus:outline-none focus:border-[#DAA520]"
            >
              <option value="">All Types</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {(search || typeFilter) && (
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="text-sm text-slate-500">{filtered.length} properties found</span>
              {(search || typeFilter) && (
                <button onClick={() => { setSearch(''); setTypeFilter(''); }} className="text-xs px-3 py-1 bg-red-100 text-red-600 rounded-full flex items-center gap-1">
                  <X size={12} /> Clear Filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-20 text-slate-500">Loading properties...</div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map(p => (
                <Link key={p.id} to={`/properties/${p.id}`} className="group block" data-testid={`prop-card-${p.id}`}>
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-slate-100">
                    <div className="h-52 bg-gradient-to-br from-[#1A1A25] to-[#0A0A0F] relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Building2 size={40} className="text-[#DAA520]/20" />
                      </div>
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className="px-3 py-1 bg-[#DAA520] text-[#0A0A0F] text-xs font-bold rounded-full">{p.property_type || 'Property'}</span>
                        {p.status === 'upcoming' && <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">Upcoming</span>}
                      </div>
                      {(p.total_area || p.size || p.property_size) && (
                        <div className="absolute bottom-4 right-4">
                          <span className="px-3 py-1.5 bg-white/90 text-[#0A0A0F] text-xs font-bold rounded-lg backdrop-blur">{p.total_area || p.size || p.property_size}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h2 className="font-bold text-lg text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors mb-2 line-clamp-1">{p.property_name}</h2>
                      {p.location && (
                        <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-2">
                          <MapPin size={14} className="text-[#DAA520]" /> {p.location}
                        </div>
                      )}
                      {p.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{p.description}</p>}
                      <div className="flex items-center justify-between">
                        <div className="text-[#DAA520] font-bold text-lg">{p.price_range || `₹${(p.total_price || 0).toLocaleString('en-IN')}`}</div>
                        <span className="flex items-center text-sm font-semibold text-[#DAA520] group-hover:gap-3 gap-2 transition-all">
                          Details <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-bold text-[#0A0A0F] mb-2">No Properties Found</h3>
              <p className="text-slate-500 mb-4">We're adding new properties regularly. Contact us for upcoming projects.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#DAA520] text-[#0A0A0F] font-bold rounded-xl">Contact Us</Link>
            </div>
          )}
        </div>
      </section>
    </WebsiteLayout>
  );
}
